/**
 * Shared API client for admin panel.
 * Centralizes fetch logic, error handling, and response parsing.
 */

function resolveApiBase(): string {
  // In production, derive API URL from current hostname: admin.X.com → api.X.com
  const hostname = window.location.hostname;
  if (hostname.startsWith('admin.')) {
    const domain = hostname.replace('admin.', '');
    return `https://api.${domain}`;
  }
  // Fallback for local dev or explicit env var
  return import.meta.env.VITE_API_BASE_URL || '';
}

export const API_BASE = resolveApiBase();

export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
}

export class ApiClientError extends Error {
  public statusCode: number;
  public errorCode: string;

  constructor(message: string, statusCode: number, errorCode: string = 'API_ERROR') {
    super(message);
    this.name = 'ApiClientError';
    this.statusCode = statusCode;
    this.errorCode = errorCode;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

/**
 * Make an API request with standardized error handling
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  };

  if (body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, fetchOptions);

  if (!response.ok) {
    let errorData: { message?: string; error?: string } = {};
    try {
      errorData = await response.json();
    } catch {
      // Response might not be JSON
    }

    throw new ApiClientError(
      errorData.message || errorData.error || `Request failed with status ${response.status}`,
      response.status,
      errorData.error
    );
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Make a GET request
 */
export function apiGet<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  let url = endpoint;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') {
        searchParams.append(key, value);
      }
    }
    const queryString = searchParams.toString();
    if (queryString) {
      url = `${endpoint}?${queryString}`;
    }
  }
  return apiRequest<T>(url, { method: 'GET' });
}

/**
 * Make a POST request
 */
export function apiPost<T>(endpoint: string, body?: unknown): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'POST', body });
}

/**
 * Make a PUT request
 */
export function apiPut<T>(endpoint: string, body?: unknown): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'PUT', body });
}

/**
 * Make a DELETE request
 */
export function apiDelete<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'DELETE' });
}

/**
 * Download a file from an API endpoint
 */
export async function apiDownload(
  endpoint: string,
  filename: string,
  options: RequestOptions = {}
): Promise<void> {
  const { method = 'GET', body, headers = {} } = options;

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  };

  if (body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, fetchOptions);

  if (!response.ok) {
    let errorData: { message?: string; error?: string } = {};
    try {
      errorData = await response.json();
    } catch {
      // Response might not be JSON
    }

    throw new ApiClientError(
      errorData.message || errorData.error || `Download failed with status ${response.status}`,
      response.status,
      errorData.error
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
